import axios from 'axios';
import jwtDecode from 'jwt-decode';
import { API_URL } from '../config';

type UserRole = 'admin' | 'super_admin' | 'agent' | 'technician' | 'customer' | 'manager';

// Configuration d'Axios
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter automatiquement le token d'authentification
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  isActive: boolean;
  phone?: string;
  phoneNumber?: string; // Ajout de la propriété phoneNumber pour la rétrocompatibilité
  specialty?: string;
  createdAt?: string;
  updatedAt?: string;
  lastLogin?: string;
  profileImage?: string;
}

// Interface pour les données brutes de l'API
interface ApiUserData {
  id: number;
  email: string;
  first_name?: string;
  last_name?: string;
  firstName?: string;
  lastName?: string;
  role: string;
  status?: string;
  isActive?: boolean;
  phone?: string;
  specialty?: string;
  createdAt?: string;
  updatedAt?: string;
  last_login?: string;
  lastLogin?: string;
  profile_image?: string;
  profileImage?: string;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role?: UserRole;
  specialty?: string;
}

interface LoginData {
  email: string;
  password: string;
  rememberMe?: boolean;
}

class AuthService {
  private token: string | null = null;

  constructor() {
    this.token = this.getTokenFromStorage();
  }

  // Récupération du token depuis le stockage local
  private getTokenFromStorage(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }
    return localStorage.getItem('token') || sessionStorage.getItem('token');
  }

  // Récupération du token
  getToken(): string | null {
    return this.token || this.getTokenFromStorage();
  }

  // Enregistrement du token
  private setToken(token: string, rememberMe: boolean = false): void {
    this.token = token;
    if (typeof window !== 'undefined') {
      if (rememberMe) {
        localStorage.setItem('token', token);
        sessionStorage.removeItem('token');
      } else {
        sessionStorage.setItem('token', token);
        localStorage.removeItem('token');
      }
    }
  }

  // Suppression du token
  private removeToken(): void {
    this.token = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
    }
  }

  // Connexion de l'utilisateur
  async login(credentials: LoginData): Promise<{ user: User; token: string }> {
    try {
      const response = await api.post<{ data: { accessToken: string; user: ApiUserData } }>('/auth/login', {
        email: credentials.email,
        password: credentials.password
      });

      console.log('Login response:', response.data);

      if (response.data?.data?.accessToken && response.data?.data?.user) {
        const token = response.data.data.accessToken;
        const userData = response.data.data.user;
        
        // Mapper les champs pour assurer la compatibilité
        const user: User = {
          id: userData.id || 0,
          email: userData.email || '',
          firstName: userData.first_name || userData.firstName || '',
          lastName: userData.last_name || userData.lastName || '',
          role: (userData.role || 'customer').toLowerCase() as UserRole,
          isActive: userData.status === 'active',
          phone: userData.phone || '',
          specialty: userData.specialty || '',
          createdAt: userData.createdAt || new Date().toISOString(),
          updatedAt: userData.updatedAt || new Date().toISOString(),
          lastLogin: userData.last_login || userData.lastLogin,
          profileImage: userData.profile_image || userData.profileImage
        };
        
        this.setToken(token, credentials.rememberMe);
        return {
          user: user,
          token: token
        };
      }
      
      throw new Error('Réponse de connexion invalide');
    } catch (error) {
      console.error('Erreur de connexion:', error);
      throw error;
    }
  }

  // Inscription d'un nouvel utilisateur
  async register(userData: RegisterData): Promise<User> {
    try {
      const response = await api.post<{ data: User }>('/auth/register', userData);
      if (response.data?.data) {
        return response.data.data;
      }
      throw new Error('Réponse d\'inscription invalide');
    } catch (error) {
      console.error('Erreur lors de l\'inscription:', error);
      throw error;
    }
  }

  // Déconnexion de l'utilisateur
  logout(): void {
    this.removeToken();
    // Rediriger vers la page de connexion
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }

  // Vérification si l'utilisateur est connecté
  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    
    try {
      return !this.isTokenExpired(token);
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'authentification:', error);
      return false;
    }
  }

  // Récupération de l'utilisateur actuellement connecté
  async getCurrentUser(): Promise<User | null> {
    const token = this.getToken();
    if (!token) {
      console.log('Aucun token trouvé dans le stockage local');
      return null;
    }
    
    try {
      const response = await api.get<{ data: { user: ApiUserData } }>('/auth/profile');
      
      if (response.data?.data?.user) {
        const userData = response.data.data.user;
        // Mapper les champs pour assurer la compatibilité
        const user: User = {
          id: userData.id || 0,
          email: userData.email || '',
          firstName: userData.first_name || userData.firstName || '',
          lastName: userData.last_name || userData.lastName || '',
          role: (userData.role || 'customer').toLowerCase() as UserRole,
          isActive: userData.status === 'active',
          phone: userData.phone || '',
          specialty: userData.specialty || '',
          createdAt: userData.createdAt || new Date().toISOString(),
          updatedAt: userData.updatedAt || new Date().toISOString(),
          lastLogin: userData.last_login || userData.lastLogin,
          profileImage: userData.profile_image || userData.profileImage
        };
        return user;
      }
      
      throw new Error('Réponse de connexion invalide');
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'utilisateur:', error);
      return null;
    }
  }

  // Vérification des permissions
  hasRole(requiredRole: UserRole): boolean {
    // Eviter le décodage du token côté client pour la détection du rôle
    // Utilisez plutôt l'AuthContext ou des appels API sécurisés côté serveur
    const token = this.getToken();
    if (!token) return false;
    try {
      const decoded: any = jwtDecode(token);
      return decoded?.role === requiredRole;
    } catch (error) {
      // Ne pas forcer la déconnexion ici pour éviter des boucles et déconnexions involontaires
      console.warn('Décodage du token échoué, fallback sur false pour hasRole');
      return false;
    }
  }

  // Vérification de l'expiration du token
  isTokenExpired(token: string): boolean {
    try {
      const decoded: any = jwtDecode(token);
      return decoded.exp < Date.now() / 1000;
    } catch (error) {
      console.error('Erreur lors de la vérification du token:', error);
      return true;
    }
  }

  // Rafraîchissement du token
  async refreshToken(): Promise<string | null> {
    try {
      const token = this.getToken();
      if (!token) return null;
      
      const response = await api.post<{ token: string }>('/auth/refresh-token', {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data?.token) {
        this.setToken(response.data.token);
        return response.data.token;
      }
      return null;
    } catch (error) {
      console.error('Erreur lors du rafraîchissement du token:', error);
      this.logout();
      return null;
    }
  }

  // Mise à jour du profil utilisateur
  async updateProfile(profileData: { profile_image?: string; [key: string]: any }): Promise<{ user: User; profile?: any }> {
    try {
      const token = this.getToken();
      if (!token) {
        throw new Error('Token non trouvé');
      }

      const response = await api.put<{ data: { user: User; profile?: any } }>('/auth/profile', profileData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data?.data) {
        return response.data.data;
      }
      
      throw new Error('Réponse invalide du serveur');
    } catch (error) {
      console.error('Erreur lors de la mise à jour du profil:', error);
      throw error;
    }
  }

  // Changement de mot de passe
  async changePassword(passwordData: { currentPassword: string; newPassword: string }): Promise<any> {
    try {
      const token = this.getToken();
      if (!token) {
        throw new Error('Token non trouvé');
      }

      const response = await api.post('/auth/change-password', passwordData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Erreur lors du changement de mot de passe:', error);
      throw error;
    }
  }
}

// Création et exportation de l'instance unique du service
const authService = new AuthService();

export { authService };
export type { User };

export default authService;